import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/**
 * What a detail page has changed and not yet saved.
 *
 * Worked out by comparing the form against the record as it was loaded, rather than by a
 * flag every onChange has to remember to set. A flag reports a field typed into and
 * changed back as dirty, and reports a field changed by code as clean; comparison gets
 * both right, and costs nothing on forms this size.
 */
export interface UnsavedChanges<T> {
  /** True when anything on the form differs from what was loaded. */
  isDirty: boolean
  /** Which keys differ, so the page can tint them and the prompt can name them. */
  fields: (keyof T & string)[]
  changed: (key: keyof T & string) => boolean
  /**
   * Accepts the current form as the new baseline. Called after a successful save, so the
   * page is clean during the moment between the save landing and the reload arriving.
   */
  accept: () => void
  /** Puts every field back to the loaded values. */
  discard: () => void
}

/**
 * Two values a person would read as the same thing.
 *
 * An empty box is an empty box whether the record says null, undefined or "": an input
 * bound with `value ?? ''` writes "" the moment somebody types into an empty field and
 * deletes it again, and calling that a change would prompt about work nobody did.
 */
function same(left: unknown, right: unknown): boolean {
  if (left === right) return true

  const leftEmpty = left === null || left === undefined || left === ''
  const rightEmpty = right === null || right === undefined || right === ''
  if (leftEmpty || rightEmpty) return leftEmpty && rightEmpty

  // Arrays and nested objects on these forms are small and are replaced wholesale rather
  // than mutated, so a structural comparison is both cheap and honest.
  if (typeof left === 'object' && typeof right === 'object') {
    return JSON.stringify(left) === JSON.stringify(right)
  }

  return false
}

/**
 * Which of `watch` differ between the form and the record it was loaded from.
 *
 * Separate from the hook so the comparison can be read and tested on its own - it is the
 * part that decides whether somebody is warned, and getting it wrong either nags about
 * nothing or loses work silently.
 */
export function changedFields<T extends object>(
  form: Partial<T>,
  baseline: Partial<T> | undefined,
  watch?: readonly (keyof T & string)[],
): (keyof T & string)[] {
  if (!baseline) return []

  const keys = watch ?? (Object.keys(baseline) as (keyof T & string)[])

  return keys.filter((key) => !same(form[key], baseline[key]))
}

/**
 * @param form   what the page is editing.
 * @param loaded the record as the server last returned it. A new object each load, which
 *               is what re-baselines the comparison after a reload.
 * @param apply  how to put values back onto the form, for discard.
 * @param watch  the keys worth comparing. Defaults to every key on the loaded record,
 *               which includes fields the form never edits - pass this on a page whose
 *               record carries collections or computed flags that are not the form.
 */
export function useUnsavedChanges<T extends object>(
  form: Partial<T>,
  loaded: T | undefined,
  apply: (values: Partial<T>) => void,
  watch?: readonly (keyof T & string)[],
): UnsavedChanges<T> {
  const [baseline, setBaseline] = useState<Partial<T> | undefined>(loaded)

  useEffect(() => {
    if (loaded) setBaseline(loaded)
  }, [loaded])

  // Held in a ref so accept() can read what is on the form now without the callback
  // changing identity on every keystroke.
  const formRef = useRef(form)
  formRef.current = form

  const fields = useMemo(() => changedFields(form, baseline, watch), [form, baseline, watch])

  const accept = useCallback(() => setBaseline({ ...formRef.current }), [])

  const discard = useCallback(() => {
    if (baseline) apply({ ...baseline })
  }, [baseline, apply])

  return {
    isDirty: fields.length > 0,
    fields,
    changed: (key) => fields.includes(key),
    accept,
    discard,
  }
}
